/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import type { Service, User, UserExtra } from '@ahm/api-wrappers-core';
import { useMemo } from 'react';
import { STATUS_VERIFY_EKYC, USER_TYPE } from '@/lib/constants';

export type UserWithEKYC = User & {
  extra: UserExtra & {
    ekyc_by?: string;
    store_image_url?: string;
    store_image_status?: string;
    online_store_url?: string;
    online_store_url_status?: string;
    business_license_front_image_url?: string;
    business_license_front_image_status?: string;
    business_license_back_image_url?: string;
    business_license_back_image_status?: string;
  };
};

export enum VerifyStatus {
  SUCCESS = 'success',
  VERIFYING = 'verifying',
  FAIL = 'fail',
}

export enum VerifyState {
  VERIFY_INFO = 'Verify info',
  IDENTIFICATION = 'Identification',
  VERIFY_SME_CORPS = 'Verify SME/CORPS',
}

export enum EKYCProvider {
  HYPERVERGE = 'HYPERVERGE',
  FPT = 'FPT',
  FPT_AI = 'FPT.AI',
}

export enum DetectMaxCodSource {
  USER = 'user',
  SERVICE = 'service',
}

const isEKYCApproved = (user: UserWithEKYC): boolean => {
  if (!user.extra || !user.extra?.ekyc_by || !user.extra?.documents)
    return false;

  const isHypervergeApproved =
    user.extra.ekyc_by === EKYCProvider.HYPERVERGE &&
    user.extra.documents.length >= 3 &&
    user.extra.documents
      .slice(0, 3)
      .every((ekyc) => ekyc.status === STATUS_VERIFY_EKYC.APPROVED);

  const isFPTApproved =
    [EKYCProvider.FPT, EKYCProvider.FPT_AI].includes(
      user.extra.ekyc_by as EKYCProvider
    ) &&
    user.extra.documents.length >= 4 &&
    user.extra.documents
      .slice(0, 4)
      .every((ekyc) => ekyc.status === STATUS_VERIFY_EKYC.APPROVED);

  return isHypervergeApproved || isFPTApproved;
};

const getSMEStatus = (user: UserWithEKYC): VerifyStatus => {
  if (!user.extra) return VerifyStatus.FAIL;

  const {
    // store_image_url,
    store_image_status,
    // online_store_url,
    online_store_url_status,
  } = user.extra;

  if (
    store_image_status === STATUS_VERIFY_EKYC.APPROVED ||
    online_store_url_status === STATUS_VERIFY_EKYC.APPROVED
  )
    return VerifyStatus.SUCCESS;

  if (
    !store_image_status ||
    store_image_status === STATUS_VERIFY_EKYC.IN_REVIEW ||
    !online_store_url_status ||
    online_store_url_status === STATUS_VERIFY_EKYC.IN_REVIEW
  ) {
    return VerifyStatus.VERIFYING;
  }

  return VerifyStatus.FAIL;
};

const getCORPStatus = (user: UserWithEKYC): VerifyStatus => {
  if (!user.extra) return VerifyStatus.FAIL;

  const {
    // business_license_front_image_url,
    business_license_front_image_status,
    // business_license_back_image_url,
    business_license_back_image_status,
  } = user.extra;

  if (
    business_license_front_image_status === STATUS_VERIFY_EKYC.APPROVED &&
    business_license_back_image_status === STATUS_VERIFY_EKYC.APPROVED
  ) {
    return VerifyStatus.SUCCESS;
  }

  if (
    // business_license_front_image_url &&
    // business_license_back_image_url &&
    (!business_license_front_image_status ||
      business_license_front_image_status === STATUS_VERIFY_EKYC.IN_REVIEW) &&
    (!business_license_back_image_status ||
      business_license_back_image_status === STATUS_VERIFY_EKYC.IN_REVIEW)
  ) {
    return VerifyStatus.VERIFYING;
  }

  return VerifyStatus.FAIL;
};

const detectMaxCod = (
  user: User,
  service: Service,
  codValue: number
): { value: number; source: string } => {
  const userMaxCod = user?.max_cod || 0;
  const serviceMaxCod = service?.max_cod || 0;

  if (!userMaxCod && serviceMaxCod && codValue > serviceMaxCod) {
    return { value: serviceMaxCod, source: DetectMaxCodSource.SERVICE };
  }

  if (!serviceMaxCod && userMaxCod && codValue > userMaxCod) {
    return { value: userMaxCod, source: DetectMaxCodSource.USER };
  }

  // If both limits exist, use the more restrictive one
  if (userMaxCod && serviceMaxCod) {
    if (codValue > userMaxCod) {
      return { value: userMaxCod, source: DetectMaxCodSource.USER };
    }
    if (codValue > serviceMaxCod) {
      return { value: serviceMaxCod, source: DetectMaxCodSource.SERVICE };
    }
  }

  return { value: 0, source: '' };
};

const detectUserState = (
  user: UserWithEKYC
): { state: string; disabled: boolean } => {
  if (!user) {
    return { state: VerifyState.VERIFY_INFO, disabled: true };
  }

  const isEKYCDone = isEKYCApproved(user);

  if (!user.user_type)
    return { state: VerifyState.VERIFY_INFO, disabled: true };

  // NOTE: 2 special cases:
  // for SME and CORPORATION
  // user verify account success but not EKYC --> priority return getSMEStatus or getCORPStatus

  if (!isEKYCDone) {
    // with INDIVIDUAL user, if verified, return VerifyStatus.SUCCESS
    const isIndividualOrVerified =
      user.user_type === USER_TYPE.INDIVIDUAL && user.verified === true;

    const status = (() => {
      if (user.user_type === USER_TYPE.SME) return getSMEStatus(user);
      if (user.user_type === USER_TYPE.CORPORATION) return getCORPStatus(user);
      return isIndividualOrVerified ? VerifyStatus.SUCCESS : null;
    })();

    if (status === VerifyStatus.SUCCESS) {
      return { state: status, disabled: false };
    }

    return { state: VerifyState.IDENTIFICATION, disabled: true };
  }

  switch (user.user_type) {
    case USER_TYPE.INDIVIDUAL:
      return { state: VerifyState.VERIFY_SME_CORPS, disabled: false };
    case USER_TYPE.SME:
      return { state: getSMEStatus(user), disabled: false };
    case USER_TYPE.CORPORATION:
      return { state: getCORPStatus(user), disabled: false };
    default:
      return { state: VerifyState.VERIFY_INFO, disabled: true };
  }
};

const useDetectCODState = (user: UserWithEKYC) => {
  return useMemo(() => detectUserState(user), [user]);
};

const useDetectMaxCod = (user: User, service: Service, codValue: number) => {
  return useMemo(
    () => detectMaxCod(user, service, codValue),
    [user, service, codValue]
  );
};

export { useDetectCODState, useDetectMaxCod };
